/* aa-pdp-mobile-adapt.js — PDP MOBILE ONLY (additive, no vendor edits).

   The mobile header is transparent and OVERLAYS the gallery, and the slide index sits
   on the photo. Both must read on any image. mix-blend-mode gives a continuous invert
   that muddies to GREY over mid-tone photo areas — so instead we sample the luminance
   of the exact region behind each element and toggle a class for a clean BINARY:
     · header  → `aa-pdp-hdr--light` (white marks over dark areas) / `aa-pdp-hdr--dark`
     · index   → `aa-idx--light` (white bars) / `aa-idx--dark` (black bars)
   Colours live in custom-aa.css; this file only decides which class is on.

   The index depends only on which slide is active (it rides 60px up from the image
   bottom). The header is fixed, so what's behind it changes with scroll too — hence we
   re-sample on scroll as well as on slide change.

   Reading image pixels needs a CORS-clean copy (crossorigin + canvas). Shopify's image
   CDN serves CORS, but if a read is ever blocked we fall back to "light background →
   dark marks" so nothing breaks. */
(function () {
  'use strict';

  if (!document.body.classList.contains('template-product')) return;

  var MOBILE = window.matchMedia('(max-width: 989.98px)'); // PDP mobile gallery now runs up to the 990 desktop breakpoint
  var THRESH = 0.6;            // luminance > THRESH ⇒ light background ⇒ DARK marks
  var ctx = null;             // 1×1 sampling canvas
  var corsCache = {};         // src → { img, ok }
  var started = false;

  function sampleCtx() {
    if (!ctx) {
      var c = document.createElement('canvas');
      c.width = c.height = 1;
      ctx = c.getContext('2d', { willReadFrequently: true });
    }
    return ctx;
  }

  /* A CORS-enabled copy of `src` so the canvas isn't tainted. Invokes cb(img | null). */
  function corsImage(src, cb) {
    var entry = corsCache[src];
    if (entry) {
      if (entry.img.complete) cb(entry.ok ? entry.img : null);
      else entry.img.addEventListener('load', function () { cb(entry.ok ? entry.img : null); });
      return;
    }
    var img = new Image();
    entry = corsCache[src] = { img: img, ok: false };
    img.crossOrigin = 'anonymous';
    img.onload = function () { entry.ok = true; cb(img); };
    img.onerror = function () { entry.ok = false; cb(null); };
    img.src = src;
  }

  /* Average relative luminance (0..1) of a fractional region of img, or null on a
     tainted/failed read. fx,fy,fw,fh are fractions (0..1) of the natural image. */
  function regionLuminance(img, fx, fy, fw, fh) {
    var nw = img.naturalWidth, nh = img.naturalHeight;
    if (!nw || !nh) return null;
    var sx = Math.max(0, Math.min(nw - 1, fx * nw));
    var sy = Math.max(0, Math.min(nh - 1, fy * nh));
    var sw = Math.max(1, Math.min(nw - sx, fw * nw));
    var sh = Math.max(1, Math.min(nh - sy, fh * nh));
    var c = sampleCtx();
    try {
      c.clearRect(0, 0, 1, 1);
      c.drawImage(img, sx, sy, sw, sh, 0, 0, 1, 1);   // downscale region → 1px average
      var d = c.getImageData(0, 0, 1, 1).data;
      return (0.2126 * d[0] + 0.7152 * d[1] + 0.0722 * d[2]) / 255;
    } catch (e) {
      return null;                                     // CORS-tainted
    }
  }

  function galleryRoot() {
    return document.querySelector('.main-product-mobile-section-wrapper') ||
           document.querySelector('.product__media-wrapper');
  }
  function activeImg() {
    var root = galleryRoot();
    if (!root) return null;
    return root.querySelector('.swiper-slide-active img') ||
           root.querySelector('.product__media-item img') ||
           root.querySelector('img');
  }
  function header() { return document.querySelector('.shopify-section-header'); }
  function indexWrap() {
    /* The index (pagination) is relocated into `.aa-pdp-stickybar` — a child of
       `.product__main`, OUTSIDE the gallery wrapper — by the sticky-bar IIFE below. So
       search the whole product main, not just the gallery root, or the sampling no-ops and
       the index stops adapting to the photo behind it. */
    var main = document.querySelector('.product__main') || galleryRoot();
    return main ? main.querySelector('.product__pagination-wrapper') : null;
  }

  /* isLightBg true ⇒ background is light ⇒ use the DARK mark class. */
  function applyState(el, lightCls, darkCls, isLightBg) {
    if (!el) return;
    el.classList.toggle(darkCls, !!isLightBg);
    el.classList.toggle(lightCls, !isLightBg);
  }

  var pending = false;
  function update() {
    pending = false;
    if (!MOBILE.matches) {
      /* Left mobile (e.g. resized to desktop): strip the mobile-only adaptation classes
         so the desktop header/index render from their own styles, not a stale state. */
      var h = header(), ix = indexWrap();
      if (h) h.classList.remove('aa-pdp-hdr--light', 'aa-pdp-hdr--dark');
      if (ix) ix.classList.remove('aa-idx--light', 'aa-idx--dark');
      return;
    }
    var img = activeImg();
    var src = img && (img.currentSrc || img.src);
    if (!src) return;
    var hdr = header(), idx = indexWrap();

    corsImage(src, function (ci) {
      /* INDEX — sample the photo strip actually BEHIND the index (it's been relocated above the
         title/price bar, so use its real rect vs the image rather than a fixed offset). */
      if (idx && img) {
        var L = null;
        if (ci) {
          var ir = img.getBoundingClientRect();
          var idr = idx.getBoundingClientRect();
          var top = Math.max(idr.top, ir.top), bottom = Math.min(idr.bottom, ir.bottom);
          if (ir.height > 0 && bottom > top) {
            var fy0 = (top - ir.top) / ir.height;
            var fy1 = (bottom - ir.top) / ir.height;
            L = regionLuminance(ci, 0.25, fy0, 0.5, Math.max(0.01, fy1 - fy0));
          } else {
            L = 1;                                       // index not over the photo → dark bars
          }
        }
        if (L === null) L = 1;                           // fallback: dark bars on white
        applyState(idx, 'aa-idx--light', 'aa-idx--dark', L > THRESH);
      }

      /* HEADER — the photo strip currently behind the fixed header band. */
      if (hdr && img) {
        var L2 = null;
        if (ci) {
          var hr = hdr.getBoundingClientRect();
          var ir = img.getBoundingClientRect();
          var bandH = hr.height || 60;
          var top = Math.max(0, ir.top), bottom = Math.min(bandH, ir.bottom);
          if (bottom > top && ir.height > 0) {
            var fy0 = (top - ir.top) / ir.height;
            var fy1 = (bottom - ir.top) / ir.height;
            L2 = regionLuminance(ci, 0.1, fy0, 0.8, Math.max(0.01, fy1 - fy0));
          } else {
            L2 = 1;                                       // scrolled past image → white info → dark
          }
        }
        if (L2 === null) L2 = 1;                          // fallback dark marks
        applyState(hdr, 'aa-pdp-hdr--light', 'aa-pdp-hdr--dark', L2 > THRESH);
      }
    });
  }

  function schedule() {
    if (pending) return;
    pending = true;
    (window.requestAnimationFrame || window.setTimeout)(update);
  }

  /* The mobile gallery (full_horizontal_slider) is a Swiper, but it's initialised while
     hidden/zero-width (it's the inactive wrapper at desktop-width load) and Madrid leaves
     Swiper's observer/observeParents OFF — so when it becomes visible it never recomputes
     and only the first slide is laid out (nothing to swipe). Recompute it ourselves. */
  function refreshSwiper() {
    if (!MOBILE.matches) return;
    var root = document.querySelector('.main-product-mobile-section-wrapper');
    var el = root && root.querySelector('.js-media-mobile-list');
    if (el && el.swiper && typeof el.swiper.update === 'function') {
      try {
        var sw = el.swiper;
        // Madrid sets slidesPerView 1.1 at >=750px (a peek). With one image the 74px peek shows
        // as white on the right. Force full-width slides (YSL-style) at every width.
        sw.params.slidesPerView = 1;
        sw.params.spaceBetween = 0;
        if (sw.params.breakpoints) {
          Object.keys(sw.params.breakpoints).forEach(function (bp) {
            sw.params.breakpoints[bp].slidesPerView = 1;
            sw.params.breakpoints[bp].spaceBetween = 0;
          });
        }
        sw.update();
      } catch (e) { /* not ready yet */ }
    }
  }
  /* swiper.update() alone doesn't fix a gallery that LOADS at tablet width (Swiper caches a
     too-narrow slide width → white gap on the right). Dispatching a real resize makes Swiper's
     own resize handler fully recompute — same as the manual resize that fixes it. Used only at
     init (NOT on scroll). */
  function kick() {
    refreshSwiper();
    try { window.dispatchEvent(new Event('resize')); } catch (e) {}
  }

  function start() {
    if (started || !MOBILE.matches) return;
    started = true;
    schedule();
    refreshSwiper();
    /* The swiper may finish initialising after us, so retry a few times (kick = update + resize). */
    [250, 700, 1500].forEach(function (t) { window.setTimeout(kick, t); });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', function () { schedule(); refreshSwiper(); });
    window.addEventListener('orientationchange', function () { schedule(); refreshSwiper(); });

    /* Re-sample on slide change — observe the SLIDES container only (the index wrapper
       is a sibling of .swiper-wrapper, so toggling its class can't re-trigger us). */
    var root = galleryRoot();
    var slides = root && root.querySelector('.swiper-wrapper');
    if (slides && 'MutationObserver' in window) {
      new MutationObserver(schedule).observe(slides, {
        attributes: true, subtree: true, attributeFilter: ['class']
      });
    }
    /* Images may decode after init. */
    if (root) root.querySelectorAll('img').forEach(function (im) {
      if (!im.complete) im.addEventListener('load', schedule, { once: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  window.addEventListener('load', function () { schedule(); kick(); window.setTimeout(kick, 400); });
  MOBILE.addEventListener('change', function () {
    if (MOBILE.matches) { start(); refreshSwiper(); } else schedule();
  });
})();

/* PDP mobile: relocate the TITLE + SUBTITLE + PRICE block into the gallery container as one
   `.aa-pdp-stickybar` piece, so CSS `position: sticky; bottom: 0` pins it to the bottom of the
   viewport over the gallery and settles it into place as you scroll the gallery up (YSL
   `.fnrdLH` behaviour). On desktop the pieces are restored to their original spots in the info
   column. Placeholder comment nodes mark the home positions so restore is exact. */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  var MQ = window.matchMedia('(max-width: 989.98px)');
  var content = document.querySelector('.product__info-main_content');
  var main = document.querySelector('.product__main');
  if (!content || !main) return;

  var title = content.querySelector('.product__title__wrapper');
  var subtitle = content.querySelector('.product__text.subtitle');
  var price = content.querySelector('.price-wrapper');
  var pieces = [title, subtitle, price].filter(Boolean); // ordered: title, subtitle, price
  if (!pieces.length) return;
  pieces.forEach(function (el) {
    if (!el.__aaPh) el.__aaPh = document.createComment('aa-pdp-piece');
  });
  // The gallery index (pagination) moves into the bar too, so CSS can place it a fixed distance
  // ABOVE the bar and it TRACKS the bar whether the bar is in flow or overlaying the gallery
  // (a viewport-fixed index fails because the sticky bar isn't always at the viewport bottom).
  var idx = main.querySelector('.product__pagination-wrapper.product__pagination-mobile_sliding_bar-wrapper');
  if (idx && !idx.__aaPh) idx.__aaPh = document.createComment('aa-pdp-idx');

  var bar = null, inner = null;
  function toBar() {
    if (!bar) {
      bar = document.createElement('div'); bar.className = 'aa-pdp-stickybar';
      // Inner holds the text — the bar stays full-width (white spans the viewport) while the
      // inner can be inset (6/8) on tablet so only the TEXT carries the margin.
      inner = document.createElement('div'); inner.className = 'aa-pdp-stickybar__inner';
      bar.appendChild(inner);
    }
    pieces.forEach(function (el) {
      if (!el.__aaPh.parentNode) el.parentNode.insertBefore(el.__aaPh, el); // mark home once
      inner.appendChild(el);
    });
    if (idx) {
      if (!idx.__aaPh.parentNode) idx.parentNode.insertBefore(idx.__aaPh, idx);
      bar.appendChild(idx); // in the FULL-WIDTH bar (not the inset inner); positioned absolute
    }
    if (bar.parentNode !== main) main.appendChild(bar);
  }
  function restore() {
    pieces.forEach(function (el) {
      if (el.__aaPh.parentNode) el.__aaPh.parentNode.insertBefore(el, el.__aaPh);
    });
    if (idx && idx.__aaPh.parentNode) idx.__aaPh.parentNode.insertBefore(idx, idx.__aaPh);
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
  }
  function apply() { if (MQ.matches) toBar(); else restore(); }

  apply();
  MQ.addEventListener('change', apply);
})();

/* AA: variant dropdown — release focus when an option is picked. The open state also
   matches `ul:focus-within` (vendor + custom CSS keep the panel open while an input
   inside has focus, for keyboard users), so after a tap/click the focused radio held
   the panel open even though global.js removed `.active`. Blur it on the same click so
   the close transition can actually run (mobile sheet slides down, desktop list closes). */
(function () {
  document.addEventListener('click', function (e) {
    var li = e.target.closest ? e.target.closest('.dropdown-select ul li') : null;
    if (!li) return;
    setTimeout(function () {
      var ae = document.activeElement;
      if (ae && ae.closest && ae.closest('.dropdown-select ul')) ae.blur();
    }, 0);
  });
})();
